// Hand-maintained — override generation

import {
  getAdminBrandIdentities,
  getAdminBrandIdentitiesById,
  postAdminBrandIdentities,
  patchAdminBrandIdentitiesById,
  deleteAdminBrandIdentitiesById,
  getAdminBrandIdentitiesByTenantByTenantId,
  getAdminBrandIdentitiesByWorkspaceByWorkspaceId,
  getAdminBrandIdentitiesDefaultTenantByTenantId,
  getAdminBrandIdentitiesDefaultWorkspaceByWorkspaceId,
  patchAdminBrandIdentitiesByIdSetDefault,
  patchAdminBrandIdentitiesByIdUnsetDefault,
} from "../_internal/sdk.gen";
import type { BrandIdentity } from "../_internal/types.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";

/** Attributes accepted when creating a brand identity. */
export type CreateBrandIdentityAttributes = {
  /** Required. Display name for this brand identity. */
  name: string;
  /** Tenant UUID — required for tenant-scoped identities. */
  tenant_id: string;
  /** Optional workspace UUID — set for workspace-level overrides, omit for tenant-level. */
  workspace_id?: string;
  /** Brand voice description (e.g. "Professional but approachable"). */
  voice?: string;
  /** Brand values as an array of strings (e.g. ["innovation", "integrity"]). */
  values?: string[];
  /** Target audience description. */
  target_audience?: string;
  /** Industry vertical. */
  industry?: string;
  /** Brand website URL. */
  website_url?: string;
  /** Logo image URL. */
  logo_url?: string;
  /** Color palette map (e.g. { primary: "#0066CC", secondary: "#FF0000" }). */
  color_palette?: Record<string, string>;
  /** General description of this brand identity. */
  description?: string;
  /** Whether this is the default identity at its scope level. */
  is_default?: boolean;
};

/** Attributes accepted when updating a brand identity (PATCH semantics). */
export type UpdateBrandIdentityAttributes = Partial<
  Omit<CreateBrandIdentityAttributes, "workspace_id" | "tenant_id">
>;

export function createBrandIdentitiesNamespace(rb: RequestBuilder) {
  return {
    /**
     * List all brand identities visible to the current actor.
     *
     * @param options - Optional request options (pagination, filters, sorting).
     * @returns A list of `BrandIdentity` records.
     *
     * @example
     * ```ts
     * const identities = await admin.brandIdentities.list();
     * ```
     */
    list: async (options?: RequestOptions): Promise<BrandIdentity[]> => {
      return rb.execute<BrandIdentity[]>(getAdminBrandIdentities, {}, options);
    },

    /**
     * Get a brand identity by ID.
     *
     * @param id - The UUID of the brand identity.
     * @param options - Optional request options.
     * @returns The `BrandIdentity` record.
     *
     * @example
     * ```ts
     * const identity = await admin.brandIdentities.get("brand-uuid");
     * ```
     */
    get: async (
      id: string,
      options?: RequestOptions,
    ): Promise<BrandIdentity> => {
      return rb.execute<BrandIdentity>(
        getAdminBrandIdentitiesById,
        { path: { id } },
        options,
      );
    },

    /**
     * Create a new brand identity.
     *
     * @param attributes - Brand identity attributes.
     * @param options - Optional request options.
     * @returns The created `BrandIdentity`.
     *
     * @example
     * ```ts
     * const identity = await admin.brandIdentities.create({
     *   name: "Corporate Brand",
     *   primary_color: "#0066CC",
     * });
     * ```
     */
    create: async (
      attributes: CreateBrandIdentityAttributes,
      options?: RequestOptions,
    ): Promise<BrandIdentity> => {
      const {
        name,
        tenant_id,
        workspace_id,
        voice,
        values,
        target_audience,
        industry,
        website_url,
        logo_url,
        color_palette,
        description,
        is_default,
      } = attributes;
      return rb.execute<BrandIdentity>(
        postAdminBrandIdentities,
        {
          body: {
            data: {
              type: "brand-identity",
              attributes: {
                name,
                tenant_id,
                workspace_id,
                voice,
                values,
                target_audience,
                industry,
                website_url,
                logo_url,
                color_palette,
                description,
                is_default,
              },
            },
          },
        },
        options,
      );
    },

    /**
     * Update a brand identity.
     *
     * @param id - The UUID of the brand identity.
     * @param attributes - Fields to update.
     * @param options - Optional request options.
     * @returns The updated `BrandIdentity`.
     *
     * @example
     * ```ts
     * const identity = await admin.brandIdentities.update("brand-uuid", {
     *   primary_color: "#FF0000",
     * });
     * ```
     */
    update: async (
      id: string,
      attributes: UpdateBrandIdentityAttributes,
      options?: RequestOptions,
    ): Promise<BrandIdentity> => {
      const {
        name,
        voice,
        values,
        target_audience,
        industry,
        website_url,
        logo_url,
        color_palette,
        description,
        is_default,
      } = attributes;
      return rb.execute<BrandIdentity>(
        patchAdminBrandIdentitiesById,
        {
          path: { id },
          body: {
            data: {
              id,
              type: "brand-identity",
              attributes: {
                name,
                voice,
                values,
                target_audience,
                industry,
                website_url,
                logo_url,
                color_palette,
                description,
                is_default,
              },
            },
          },
        },
        options,
      );
    },

    /**
     * Delete a brand identity.
     *
     * @param id - The UUID of the brand identity.
     * @param options - Optional request options.
     * @returns `true` on success.
     *
     * @example
     * ```ts
     * await admin.brandIdentities.delete("brand-uuid");
     * ```
     */
    delete: async (id: string, options?: RequestOptions): Promise<true> => {
      return rb.executeDelete(
        deleteAdminBrandIdentitiesById,
        { path: { id } },
        options,
      );
    },

    /**
     * List brand identities for a specific tenant.
     *
     * @param tenantId - The UUID of the tenant.
     * @param options - Optional request options.
     * @returns A list of `BrandIdentity` records.
     *
     * @example
     * ```ts
     * const identities = await admin.brandIdentities.listByTenant("tenant-uuid");
     * ```
     */
    listByTenant: async (
      tenantId: string,
      options?: RequestOptions,
    ): Promise<BrandIdentity[]> => {
      return rb.execute<BrandIdentity[]>(
        getAdminBrandIdentitiesByTenantByTenantId,
        { path: { tenant_id: tenantId } },
        options,
      );
    },

    /**
     * List brand identities for a specific workspace.
     *
     * @param workspaceId - The UUID of the workspace.
     * @param options - Optional request options.
     * @returns A list of `BrandIdentity` records.
     *
     * @example
     * ```ts
     * const identities = await admin.brandIdentities.listByWorkspace("workspace-uuid");
     * ```
     */
    listByWorkspace: async (
      workspaceId: string,
      options?: RequestOptions,
    ): Promise<BrandIdentity[]> => {
      return rb.execute<BrandIdentity[]>(
        getAdminBrandIdentitiesByWorkspaceByWorkspaceId,
        { path: { workspace_id: workspaceId } },
        options,
      );
    },

    /**
     * Get the default brand identity for a tenant.
     *
     * @param tenantId - The UUID of the tenant.
     * @param options - Optional request options.
     * @returns The default `BrandIdentity` for the tenant.
     *
     * @example
     * ```ts
     * const identity = await admin.brandIdentities.getDefaultForTenant("tenant-uuid");
     * ```
     */
    getDefaultForTenant: async (
      tenantId: string,
      options?: RequestOptions,
    ): Promise<BrandIdentity> => {
      return rb.execute<BrandIdentity>(
        getAdminBrandIdentitiesDefaultTenantByTenantId,
        { path: { tenant_id: tenantId } },
        options,
      );
    },

    /**
     * Get the default brand identity for a workspace.
     *
     * @param workspaceId - The UUID of the workspace.
     * @param options - Optional request options.
     * @returns The default `BrandIdentity` for the workspace.
     *
     * @example
     * ```ts
     * const identity = await admin.brandIdentities.getDefaultForWorkspace("workspace-uuid");
     * ```
     */
    getDefaultForWorkspace: async (
      workspaceId: string,
      options?: RequestOptions,
    ): Promise<BrandIdentity> => {
      return rb.execute<BrandIdentity>(
        getAdminBrandIdentitiesDefaultWorkspaceByWorkspaceId,
        { path: { workspace_id: workspaceId } },
        options,
      );
    },

    /**
     * Set a brand identity as the default.
     *
     * @param id - The UUID of the brand identity.
     * @param options - Optional request options.
     * @returns The updated `BrandIdentity`.
     *
     * @example
     * ```ts
     * const identity = await admin.brandIdentities.setDefault("brand-uuid");
     * ```
     */
    setDefault: async (
      id: string,
      options?: RequestOptions,
    ): Promise<BrandIdentity> => {
      return rb.execute<BrandIdentity>(
        patchAdminBrandIdentitiesByIdSetDefault,
        {
          path: { id },
          body: { data: { id, type: "brand-identity", attributes: {} } },
        },
        options,
      );
    },

    /**
     * Unset a brand identity as the default.
     *
     * @param id - The UUID of the brand identity.
     * @param options - Optional request options.
     * @returns The updated `BrandIdentity`.
     *
     * @example
     * ```ts
     * const identity = await admin.brandIdentities.unsetDefault("brand-uuid");
     * ```
     */
    unsetDefault: async (
      id: string,
      options?: RequestOptions,
    ): Promise<BrandIdentity> => {
      return rb.execute<BrandIdentity>(
        patchAdminBrandIdentitiesByIdUnsetDefault,
        {
          path: { id },
          body: { data: { id, type: "brand-identity", attributes: {} } },
        },
        options,
      );
    },
  };
}
